/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./ExpressPayment.css";
import PropTypes from "prop-types";
import {
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { isPaymentMethodHiddenOnProject, PROJECT_LINK_KEY } from "../../assets/helper.js";

class ExpressPayment extends Component {
  constructor(props) {
    super(props);

    try {
      if (this.props.state === null || this.props.state === undefined) {
        this.props.history.push("/SelectMission");
      }
    } catch (e) {
      this.props.history.push("/SelectMission");
    }

    const dataObj = this.props.state;

    // console.log("ExpressPayment - Show dataObj.amount");
    // console.log(dataObj.amount);

    this.state = {
      ...dataObj,
      ...{
        canMakePayment: false,
        paymentRequest: null,
      },
    };

    this.initializePaymentRequest = this.initializePaymentRequest.bind(this);
  }

  componentDidMount() {
    this.initializePaymentRequest();
  }

  // Building the request is deferred out of the constructor because
  // `this.props.stripe` comes from <Elements>, which starts null and resolves in
  // a passive effect - so it is null whenever this component mounts. Calling
  // stripe.paymentRequest() in the constructor threw "Cannot read properties of
  // null", which crashed the whole project page to a blank screen if the modal
  // was ever mounted before Stripe had loaded.
  initializePaymentRequest() {
    if (!this.props.stripe || this.state.paymentRequest) {
      return;
    }

    const dataObj = this.props.state;
    const chargedAmount = dataObj.coverFee && dataObj.feeCoverageEstimate?.totalAmount
      ? dataObj.feeCoverageEstimate.totalAmount
      : dataObj.amount;
    const amount = Math.round(chargedAmount * 100);
    const organizationName = dataObj.organization.organizationName;

    const paymentRequest = this.props.stripe.paymentRequest({
      country: "HK",
      currency: "hkd",
      total: {
        label: organizationName,
        amount: amount,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });

    paymentRequest.on(
      "paymentmethod",
      ({ complete, paymentMethod, ...data }) => {
        if (!paymentMethod) complete("fail");

        this.setState({
          paymentMethodId: paymentMethod.id,
          brand: paymentMethod.card.brand,
          name: paymentMethod.billing_details.name,
          email: paymentMethod.billing_details.email,
          phone: paymentMethod.billing_details.phone,
          wallet: paymentMethod.card.wallet.type,
          last4: paymentMethod.card.last4,
        });

        complete("success");
      }
    );

    paymentRequest.canMakePayment().then((result) => {
      this.setState({ canMakePayment: !!result, paymentRequest });
    }).catch(() => {
      // Wallet unavailable - leave canMakePayment false so the donor falls back
      // to the standard payment methods.
      this.setState({ paymentRequest });
    });
  }

  closeModal() {
    this.setState({
      showModal: false,
    });
  }

  componentDidUpdate(prevProps, prevState) {
    // Stripe loads asynchronously; build the request as soon as it arrives.
    if (!prevProps.stripe && this.props.stripe) {
      this.initializePaymentRequest();
    }

    // The wallet fires "paymentmethod" once, so hand off exactly once.
    // This used to live in render(), where it could re-fire on every re-render.
    if (!prevState.paymentMethodId && this.state.paymentMethodId) {
      this.completeExpressPayment();
    }
  }

  // The wallet returns a single name string, but the form collects given and
  // family names separately. Treat the final word as the family name and
  // everything before it as given name(s), so multi-part given names such as
  // "Mary Jane Watson" stay intact. Both fields remain editable by the donor.
  splitPayerName(fullName) {
    const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);

    if (parts.length === 0) {
      return { firstName: "", lastName: "" };
    }
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: "" };
    }
    return {
      firstName: parts.slice(0, -1).join(" "),
      lastName: parts[parts.length - 1],
    };
  }

  completeExpressPayment() {
    // Country is a nice-to-have for compliance/localisation only. The lookup must
    // never gate the redirect: the wallet sheet has already closed by this point,
    // so a blocked or failed request (ad-blocker, tracking prevention, offline)
    // would otherwise strand the donor on this page after a successful auth.
    fetch(
      "https://ipapi.co/json/?key=7331911097f0e92e5a024ac30c0dd33ae3232200",
      {
        method: "GET",
      }
    )
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null)
      .then((data) => {
        const { firstName, lastName } = this.splitPayerName(this.state.name);

        const dataObj = {
          ...this.props.state,
          paymentMethod: this.state.paymentMethodId,
          brand: this.state.brand,
          wallet: this.state.wallet,
          last4: this.state.last4,
          country: this.state.country,
          // PersonalDetails spreads location.state last, so this replaces its
          // empty default and lands in the inputs pre-filled but still editable.
          donorInfo: {
            firstName,
            lastName,
            orgName: "",
            email: this.state.email || "",
            memo: "",
            phoneNumber: this.state.phone || "",
          },
          // The wallet already authorised a payment method, so PersonalDetails
          // sends the donor straight to /Review instead of asking them to pick
          // a payment method again.
          fromExpressPayment: true,
        };

        if (typeof this.state.coverFee === 'boolean') {
          dataObj.coverFee = this.state.coverFee;
        }
        if (!dataObj.feeBreakdown && this.state.feeBreakdown) {
          dataObj.feeBreakdown = this.state.feeBreakdown;
        }

        dataObj.country = (data && data.country) || "HK";
        dataObj.mission.description = null;
        dataObj.receipt = true;

        // Route through PersonalDetails rather than straight to /Review so the
        // donor can correct the wallet-supplied details and fill in any custom
        // fields the organisation requires - the wallet cannot supply those.
        this.props.history.push({ pathname: "/PersonalDetails", state: dataObj });
      });
  }

  render() {
    const RenderPaymentBtn = () => {
      // Org setting, narrowed by the project's own hide list (CP-280).
      const enableStripeLink =
        this.state.organization?.enableStripeLink !== false &&
        !isPaymentMethodHiddenOnProject(this.state.mission, PROJECT_LINK_KEY);

      return this.state.canMakePayment &&
        !this.state.isRecurring &&
        !this.props.isAuth ? (
          <PaymentRequestButtonElement
            className="ExpressPayment-PaymentRequestButton"
            options={{
              paymentRequest: this.state.paymentRequest,
              wallets: {
                applePay: 'auto',
                googlePay: 'auto',
                link: enableStripeLink ? 'auto' : 'never'
              },
              style: {
              // For more details on how to style the Payment Request Button, see:
              // https://stripe.com/docs/elements/payment-request-button#styling-the-element
                paymentRequestButton: {
                  theme: "dark",
                  height: "64px",
                  type: "default",
                },
              },
            }}
          />
        ) : (
          <div className="ExpressPayment-btnLoading">
            <div className="ExpressPayment-btnLoadingTxt">Loading...</div>
          </div>
        );
    };
    if (!this.props.show) {
      return null;
    }

    return (
      <div className="ExpressPayment-Modal">
        <section className="ExpressPayment-ModalMain">
          <div className="ExpressPayment-ModalContainer">
            <div
              className="ExpressPayment-backModalContainer"
              onClick={() => this.props.handleClose()}
            >
              <div className="ExpressPayment-back">
                <div className="ExpressPayment-backBtn" />
                <div className="ExpressPayment-backText">Back</div>
              </div>
            </div>

            <div className="ExpressPayment-paymentLine1">
              You can pay faster and more securely with
            </div>

            {/* No onClick here: handleReceipt() navigates to /PersonalDetails,
                which would abandon the wallet flow. The Stripe button renders in
                its own iframe so its clicks never reach this element, but taps on
                the surrounding padding - or on the "Loading..." placeholder before
                the button mounts - would. Donors who want the standard flow have
                the explicit "Other payment methods" link below. */}
            <div className="ExpressPayment-renderPaymentBtn">
              <RenderPaymentBtn />
            </div>

            <div
              className="ExpressPayment-otherPaymentMethods"
              onClick={() => this.props.handleReceipt()}
            >
              Other payment methods
            </div>
          </div>
        </section>
      </div>
    );
  }
}

ExpressPayment.propTypes = {
  show: PropTypes.bool,
  btnColor: PropTypes.string,
  handleClose: PropTypes.func,
  state: PropTypes.object,
  handleReceipt: PropTypes.func,
  stripe: PropTypes.object,
  isAuth: PropTypes.bool,
};

// Wrapper component to use Stripe hooks with class component
function ExpressPaymentWithStripe(props) {
  const stripe = useStripe();
  const elements = useElements();

  return <ExpressPayment {...props} stripe={stripe} elements={elements} />;
}

export default ExpressPaymentWithStripe;
